import { inject, Injectable } from "@angular/core";

import { delayWhen, forkJoin, map, Observable, of, switchMap } from "rxjs";

import { LatLon } from "src/app/shared/components/bubble-map/bubble-map.component";
import { Place, PlaceAdapter } from "../models/place.model";
import { HydraoApiService } from "./core/hydrao-api.service";

@Injectable({
  providedIn: "root",
})
export class PlaceService extends HydraoApiService<Place> {
  private placeAdapter: PlaceAdapter = inject(PlaceAdapter);

  /**
   * Empty place list cache.
   * @param toplevel Top level webservice?
   */
  public emptyPlacesCache(toplevel: boolean): Observable<void> {
    let url = "/places";
    if (toplevel) {
      url += "?topLevel=true";
    }
    return this.cacheService.clear(url);
  }

  public emptyCache() {
    return forkJoin([
      this.emptyPlacesCache(true),
      this.emptyPlacesCache(false),
    ]);
  }

  public emptyPlaceAnomaliesCache(): Observable<void> {
    return this.cacheService.clear("/meters/place-anomalies");
  }

  /**
   * Return places
   */
  public getPlaces(
    toplevel: boolean,
    refresh: boolean = false
  ): Observable<Place[]> {
    let url = "/places";
    if (toplevel) {
      url += "?topLevel=true";
    }

    if (refresh) {
      //clear all caches
      return forkJoin([
        this.emptyPlacesCache(true),
        this.emptyPlacesCache(false),
      ]).pipe(
        switchMap(() => this.get(url, this.placeAdapter) as Observable<Place[]>)
      );
    } else {
      this.logger.info("returning place observable");
      return this.get(url, this.placeAdapter) as Observable<Place[]>;
    }
  }

  public getPlacesForStore(): Observable<Place[]> {
    return this.get("/places", this.placeAdapter, true, true);
  }

  /**
   * get toplevel places with anomalies
   */
  public getPlaceAnomalies(): Observable<Place[]> {
    let url = "/meters/place-anomalies";
    return this.get(url, this.placeAdapter) as Observable<Place[]>;
  }

  /**
   * Return places
   * @param placeId : the placa id.
   */
  public getPlace(
    placeId: number,
    refresh: boolean = false
  ): Observable<Place> {
    let url = "/places/" + placeId;
    if (refresh) {
      return this.cacheService
        .clear(url)
        .pipe(
          switchMap(() => this.get(url, this.placeAdapter) as Observable<Place>)
        );
    } else {
      return this.get(url, this.placeAdapter) as Observable<Place>;
    }
  }

  public addPlaceForStore(
    label: string,
    description?: string,
    location?: LatLon,
    parentId?: number
  ): Observable<Place> {
    const url = "/places";
    const data = new Place(
      null,
      label,
      null,
      description,
      location ? location.lat : null,
      location ? location.lon : null,
      0,
      parentId,
      [],
      0
    );

    return this.post(url, data, this.placeAdapter, false);
  }

  /**
   * Post new place
   * @param place The place
   */
  public postPlace(place: Place): Observable<Place> {
    return this.post("/places", place, this.placeAdapter).pipe(
      delayWhen(() => this.emptyCache())
    );
  }

  public updatePlaceForStore(
    placeId: number,
    label: string,
    description?: string,
    location?: LatLon,
    parentId?: number
  ): Observable<Place> {
    let url = "/places/" + placeId;
    let data = {
      label: label,
      description: description,
      latitude: location ? location.lat : null,
      longitude: location ? location.lon : null,
      parent_id: parentId,
      move_children: true,
    };

    return this.put(url, data, this.placeAdapter, false);
  }

  /**
   * Modify existing place
   * @param place The place
   */
  public putPlace(place: Place): Observable<Place> {
    return this.put("/places/" + place.id, place, this.placeAdapter).pipe(
      delayWhen(() => this.emptyCache())
    );
  }

  public removePlaceForStore(placeId: number) {
    let url = "/places/" + placeId;

    return this.delete(url, false);
  }

  /**
   * Delete place
   * @param placeId The place id
   */
  public deletePlace(placeId: number) {
    return this.delete("/places/" + placeId).pipe(
      delayWhen(() => this.emptyCache())
    );
  }

  // obsolete
  public computePlaceLabelFromId(placeId: number): Observable<string> {
    if (!placeId) return of($localize`no place`);

    let parentPlaces: Place[] = [];
    let findPlaceInArray = (places: Place[], placeId: number) => {
      let foundPlace = null;
      for (let i = 0; i < places.length && !foundPlace; i++) {
        if (places[i].id == placeId) {
          foundPlace = places[i];
        } else if (places[i].children) {
          foundPlace = findPlaceInArray(places[i].children, placeId);
          if (foundPlace) {
            parentPlaces.unshift(places[i]);
          }
        }
      }
      return foundPlace;
    };

    return this.getPlaces(false, false).pipe(
      map((places) => {
        let place = findPlaceInArray(places, placeId);
        if (place) {
          let placeName = "";
          for (let curPl of parentPlaces) {
            placeName += curPl.label + " / ";
          }
          placeName += place.label;
          return placeName;
        } else {
          return $localize`no place`;
        }
      })
    );
  }
}
